import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ComposedChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, Bar } from 'recharts';
import { Settings, AlertCircle, TrendingUp } from 'lucide-react';
import { useComponentMetrics, getMetricObjectiveClassName, libelleTauxConversion, libelleConversions } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';
import { marqueursDeNotes, useNotesDeGraphique, InfobulleDeNotes } from './notes/marqueursDeNotes';

// Classification des métriques GA4 : VALEURS (pour histogramme) vs RATIOS (pour courbe)
const GA4_VALUE_METRICS = ['visites', 'visiteurs', 'pages_vues', 'conv', 'transactions', 'chiffre_affaires'];
const GA4_RATIO_METRICS = ['taux_conv', 'rebond', 'duree_moy', 'pages_visite', 'panier_moyen', 'taux_transaction'];

// Mapping clé KPI → clé graphique
const GA4_METRIC_KEY_MAP = {
  visites: 'visits',
  visiteurs: 'visitors',
  pages_vues: 'pageviews',
  conv: 'conversions',
  taux_conv: 'conversionRate',
  rebond: 'bounceRate',
  duree_moy: 'avgTimeOnSite',
  pages_visite: 'actionsPerVisit',
  panier_moyen: 'panierMoyen',
  transactions: 'transactions',
  chiffre_affaires: 'totalRevenue',
  taux_transaction: 'transactionRate'
};

/**
 * GA4PerformanceChart - Composant de graphique pour les métriques Google Analytics 4
 * Style identique à Matomo/Google Ads PerformanceChart
 *
 * Métriques affichées:
 * 1. Sessions (visites)
 * 2. % Conversion (l'événement choisi sous Conversions, sinon les événements clés)
 * 3. Conversions
 * 4. Pages vues
 * 5. % de Rebond
 * 6. Durée moyenne
 * 7. Valeur de Conversion
 * 8. Pages/Session
 */

const GA4PerformanceChart = ({
  data,
  /**
   * Notes à reporter sur le graphique.
   *
   * Le mécanisme n'existait que sur les dashboards de régies : une note cochée « afficher
   * sur les graphiques » et rattachée à une propriété analytique ne s'affichait nulle part.
   */
  notes = [],
  summary,
  previousPeriodData,
  granularity = 'day',
  onGranularityChange,
  loading = false,
  KPICard,
  formatNumber,
  userRole,
  conversionConfig,
  compareEnabled = false,
  comparisonType = '',
  summaryVariations = null,
  comparisonPeriod = null,
  // Props pour la configuration des métriques
  accountId = 'global',
  // Props pour les options avancées (conversions)
  token = null,
  propertyId = null,
  onConversionConfigSave = null,
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'ga4_results'
}) => {

  const [showPerformanceChart, setShowPerformanceChart] = useState(true);
  const [selectedLineMetric, setSelectedLineMetric] = useState('conversionRate'); // RATIO par défaut
  const [selectedBarMetric, setSelectedBarMetric] = useState('conversions'); // VALEUR par défaut
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Hook pour les métriques du composant Résultats (réactif aux changements)
  const resultsMetrics = useComponentMetrics('ga4_results', accountId);

  // Fonction helper pour vérifier si une métrique est sélectionnée pour Résultats
  const isResultsMetricActive = useCallback((metricKey) => {
    return resultsMetrics.metrics.includes(metricKey);
  }, [resultsMetrics.metrics]);

  // Filtrer les métriques disponibles selon celles activées dans "Configurer les métriques"
  const enabledMetrics = resultsMetrics?.metrics || [];
  const availableValueMetrics = useMemo(() =>
    GA4_VALUE_METRICS.filter(m => enabledMetrics.includes(m)),
    [enabledMetrics]
  );
  // La courbe peut afficher TOUTES les métriques (VALEURS + RATIOS)
  const availableLineMetrics = useMemo(() =>
    [...GA4_VALUE_METRICS, ...GA4_RATIO_METRICS].filter(m => enabledMetrics.includes(m)),
    [enabledMetrics]
  );

  // Ref pour stocker le dernier chartConfig appliqué (détection de changement de template)
  const lastChartConfigRef = useRef(null);

  // Effet unique pour gérer le chartConfig ET la synchronisation des métriques
  useEffect(() => {
    const chartConfig = resultsMetrics?.chartConfig;
    // Sérialisation complète, et non le seul couple de métriques : réappliquer le même
    // template après avoir choisi une autre courbe à la main doit reprendre la main, or ce
    // couple-là est inchangé. L'horodatage posé à l'application fait la différence.
    const configKey = chartConfig ? JSON.stringify(chartConfig) : null;
    const isNewConfig = configKey && lastChartConfigRef.current !== configKey;

    // Déterminer les métriques cibles
    let targetBarMetric = selectedBarMetric;
    let targetLineMetric = selectedLineMetric;

    // Si nouveau chartConfig, utiliser ses valeurs comme cibles
    if (isNewConfig) {
      console.log(`📊 [GA4] Nouveau template détecté: bar=${chartConfig.barMetric}, line=${chartConfig.lineMetric}`);
      targetBarMetric = chartConfig.barMetric;
      targetLineMetric = chartConfig.lineMetric;
      lastChartConfigRef.current = configKey;
    }

    // Vérifier que la métrique cible de la courbe est disponible
    const targetLineKpiKey = Object.keys(GA4_METRIC_KEY_MAP).find(k => GA4_METRIC_KEY_MAP[k] === targetLineMetric);
    if (!targetLineKpiKey || !availableLineMetrics.includes(targetLineKpiKey)) {
      // Fallback : préférer un ratio, sinon une valeur
      const firstAvailableRatio = availableLineMetrics.find(m => GA4_RATIO_METRICS.includes(m));
      const firstAvailable = firstAvailableRatio || availableLineMetrics[0];
      if (firstAvailable) {
        targetLineMetric = GA4_METRIC_KEY_MAP[firstAvailable];
      }
    }

    // Vérifier que la métrique cible de l'histogramme est disponible
    const targetBarKpiKey = Object.keys(GA4_METRIC_KEY_MAP).find(k => GA4_METRIC_KEY_MAP[k] === targetBarMetric);
    if (!targetBarKpiKey || !availableLineMetrics.includes(targetBarKpiKey)) {
      // Sélectionner la première valeur disponible, sinon un ratio
      const firstAvailableValue = availableLineMetrics.find(m => GA4_VALUE_METRICS.includes(m));
      const firstAvailable = firstAvailableValue || availableLineMetrics[0];
      if (firstAvailable) {
        targetBarMetric = GA4_METRIC_KEY_MAP[firstAvailable];
      }
    }

    // Appliquer les changements si nécessaire
    if (targetBarMetric !== selectedBarMetric) {
      setSelectedBarMetric(targetBarMetric);
    }
    if (targetLineMetric !== selectedLineMetric) {
      setSelectedLineMetric(targetLineMetric);
    }
  }, [availableLineMetrics, resultsMetrics?.chartConfig]);

  // Normaliser les données de la période précédente/comparaison
  // Quand compareEnabled est true, previousPeriodData contient { summary, channels, ... }
  // Sinon, c'est directement le summary
  const prevSummary = useMemo(() => {
    if (!previousPeriodData) return null;
    return previousPeriodData.summary || previousPeriodData;
  }, [previousPeriodData]);

  // Label pour la période de comparaison
  const comparisonLabel = comparisonType === 'previous_year' ? 'année précédente' : 'période précédente';

  // Formateur de temps (secondes -> mm:ss)
  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Configuration des métriques pour la courbe
  const lineMetricConfig = useMemo(() => ({
    visits: { label: 'Sessions', color: '#82ca9d', unit: '', format: (val) => formatNumber(val) },
    visitors: { label: 'Utilisateurs', color: '#1a1a1a', unit: '', format: (val) => formatNumber(val) },
    conversionsValue: { label: 'Valeur Conv.', color: '#06b6d4', unit: '€', format: (val) => formatNumber(val, 2) },
    pageviews: { label: 'Pages vues', color: '#10b981', unit: '', format: (val) => formatNumber(val) },
    bounceRate: { label: '% de Rebond', color: '#8b5cf6', unit: '%', format: (val) => formatNumber(val, 1) },
    avgTimeOnSite: { label: 'Durée moyenne', color: '#f97316', unit: '', format: (val) => formatTime(val) },
    actionsPerVisit: { label: 'Pages/Session', color: '#ec4899', unit: '', format: (val) => formatNumber(val, 1) },
    conversionRate: { label: libelleTauxConversion(conversionConfig), color: '#f59e0b', unit: '%', format: (val) => formatNumber(val, 2) },
    conversions: { label: libelleConversions(conversionConfig), color: '#8884d8', unit: '', format: (val) => formatNumber(val) },
    panierMoyen: { label: 'Panier moyen', color: '#a855f7', unit: '€', format: (val) => formatNumber(val, 2) },
    // Métriques e-commerce
    transactions: { label: 'Transactions', color: '#10b981', unit: '', format: (val) => formatNumber(val) },
    totalRevenue: { label: 'Chiffre d\'affaires', color: '#f59e0b', unit: '€', format: (val) => formatNumber(val, 2) },
    transactionRate: { label: '% Conversion (transactions)', color: '#14b8a6', unit: '%', format: (val) => formatNumber(val, 2) }
  }), [conversionConfig, formatNumber]);

  // Fonctions pour calculer les valeurs des métriques - utilisées dans useMemo
  const getLineMetricValue = useCallback((d) => {
    switch (selectedLineMetric) {
      case 'visits': return d.visits || 0;
      case 'visitors': return d.uniqueVisitors || d.users || d.activeUsers || 0;
      case 'conversionsValue': return d.conversionsValue || 0;
      case 'pageviews': return d.pageviews || 0;
      case 'bounceRate': return d.bounceRate || 0;
      case 'avgTimeOnSite': return d.avgTimeOnSite || 0;
      case 'actionsPerVisit': return d.actionsPerVisit || 0;
      case 'conversionRate': return d.visits > 0 ? ((d.conversions || 0) / d.visits * 100) : 0;
      case 'conversions': return d.conversions || 0;
      case 'panierMoyen': return (d.conversions || 0) > 0 ? (d.conversionsValue || 0) / d.conversions : 0;
      // Métriques e-commerce
      case 'transactions': return d.transactions || 0;
      case 'totalRevenue': return d.totalRevenue || 0;
      case 'transactionRate': return d.visits > 0 ? ((d.transactions || 0) / d.visits * 100) : 0;
      default: return d.visits || 0;
    }
  }, [selectedLineMetric]);

  // Fonction pour calculer la valeur de la métrique des barres
  const getBarMetricValue = useCallback((d) => {
    switch (selectedBarMetric) {
      case 'visits': return d.visits || 0;
      case 'visitors': return d.uniqueVisitors || d.users || d.activeUsers || 0;
      case 'conversionsValue': return d.conversionsValue || 0;
      case 'pageviews': return d.pageviews || 0;
      case 'conversions': return d.conversions || 0;
      // Métriques e-commerce
      case 'transactions': return d.transactions || 0;
      case 'totalRevenue': return d.totalRevenue || 0;
      case 'transactionRate': return d.visits > 0 ? ((d.transactions || 0) / d.visits * 100) : 0;
      // RATIOs
      case 'bounceRate': return d.bounceRate || 0;
      case 'avgTimeOnSite': return d.avgTimeOnSite || 0;
      case 'actionsPerVisit': return d.actionsPerVisit || 0;
      case 'conversionRate': return d.visits > 0 ? ((d.conversions || 0) / d.visits * 100) : 0;
      case 'panierMoyen': return (d.conversions || 0) > 0 ? (d.conversionsValue || 0) / d.conversions : 0;
      default: return d.conversions || 0;
    }
  }, [selectedBarMetric]);

  // Formater les labels de date selon la granularité
  const formatDateLabel = (dateStr, gran) => {
    if (!dateStr) return '';

    switch (gran) {
      case 'week':
        return `Sem. ${dateStr}`;
      case 'month':
        if (dateStr.includes('/')) return dateStr;
        if (dateStr.includes('-') && dateStr.length === 7) {
          const [year, month] = dateStr.split('-');
          return `${month}/${year}`;
        }
        return dateStr;
      case 'quarter':
      case 'year':
        return dateStr;
      default: // day
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          const date = new Date(dateStr);
          return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        }
        return dateStr;
    }
  };

  // Données du graphique avec métrique sélectionnée
  const { survolee, position, onSurvol, onSortie } = useNotesDeGraphique();

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(d => ({
      ...d,
      lineMetricValue: getLineMetricValue(d),
      barMetricValue: getBarMetricValue(d)
    }));
  }, [data, getLineMetricValue, getBarMetricValue]);

  // Calculer les KPI à partir du summary ou des données
  const kpiData = useMemo(() => {
    // Utiliser le summary si disponible
    if (summary) {
      return {
        totalVisits: summary.totalVisits || 0,
        totalUsers: summary.totalUsers || summary.activeUsers || 0,
        totalConversionsValue: summary.conversionsValue || 0,
        totalPageviews: summary.totalPageviews || 0,
        totalConversions: summary.conversions || 0,
        avgBounceRate: summary.bounceRate || 0,
        avgTimeOnSite: summary.avgTimeOnSite || 0,
        avgActionsPerVisit: summary.actionsPerVisit || 0,
        conversionRate: summary.conversionRate || 0,
        // Métriques e-commerce
        totalTransactions: summary.transactions || 0,
        totalRevenue: summary.totalRevenue || 0,
        transactionRate: summary.transactionRate || 0
      };
    }

    // Sinon calculer depuis les données quotidiennes
    if (!data || data.length === 0) {
      return {
        totalVisits: 0,
        totalUsers: 0,
        totalConversionsValue: 0,
        totalPageviews: 0,
        totalConversions: 0,
        avgBounceRate: 0,
        avgTimeOnSite: 0,
        avgActionsPerVisit: 0,
        conversionRate: 0,
        // Métriques e-commerce
        totalTransactions: 0,
        totalRevenue: 0,
        transactionRate: 0
      };
    }

    const totals = data.reduce((acc, d) => ({
      visits: acc.visits + (d.visits || 0),
      users: acc.users + (d.users || d.activeUsers || 0),
      conversionsValue: acc.conversionsValue + (d.conversionsValue || 0),
      pageviews: acc.pageviews + (d.pageviews || 0),
      conversions: acc.conversions + (d.conversions || 0),
      // E-commerce
      transactions: acc.transactions + (d.transactions || 0),
      totalRevenue: acc.totalRevenue + (d.totalRevenue || 0),
      bounceSum: acc.bounceSum + ((d.bounceRate || 0) * (d.visits || 0)),
      timeSum: acc.timeSum + ((d.avgTimeOnSite || 0) * (d.visits || 0)),
      actionsSum: acc.actionsSum + ((d.actionsPerVisit || 0) * (d.visits || 0))
    }), { visits: 0, users: 0, conversionsValue: 0, pageviews: 0, conversions: 0, transactions: 0, totalRevenue: 0, bounceSum: 0, timeSum: 0, actionsSum: 0 });

    return {
      totalVisits: totals.visits,
      totalUsers: totals.users,
      totalConversionsValue: totals.conversionsValue,
      totalPageviews: totals.pageviews,
      totalConversions: totals.conversions,
      avgBounceRate: totals.visits > 0 ? totals.bounceSum / totals.visits : 0,
      avgTimeOnSite: totals.visits > 0 ? totals.timeSum / totals.visits : 0,
      avgActionsPerVisit: totals.visits > 0 ? totals.actionsSum / totals.visits : 0,
      conversionRate: totals.visits > 0 ? (totals.conversions / totals.visits * 100) : 0,
      // Métriques e-commerce
      totalTransactions: totals.transactions,
      totalRevenue: totals.totalRevenue,
      transactionRate: totals.visits > 0 ? (totals.transactions / totals.visits * 100) : 0
    };
  }, [data, summary]);

  // Configuration métrique courbe actuelle
  const currentLineConfig = lineMetricConfig[selectedLineMetric];

  // Configuration des barres (pour TOUTES les métriques : VALEURS + RATIOS)
  const barMetricConfig = useMemo(() => {
    const getLabel = (key, defaultLabel) => resultsMetrics.labels[key] || defaultLabel;
    const configs = {
      visits: { name: getLabel('visites', 'Sessions'), fill: '#82ca9d' },
      visitors: { name: getLabel('visiteurs', 'Utilisateurs'), fill: '#1a1a1a' },
      pageviews: { name: getLabel('pages_vues', 'Pages vues'), fill: '#10b981' },
      conversions: { name: getLabel('conv', libelleConversions(conversionConfig)), fill: '#8884d8' },
      // Métriques e-commerce
      transactions: { name: getLabel('transactions', 'Transactions'), fill: '#10b981' },
      totalRevenue: { name: getLabel('chiffre_affaires', 'Chiffre d\'affaires'), fill: '#f59e0b' },
      transactionRate: { name: getLabel('taux_transaction', '% Conversion (transactions)'), fill: '#14b8a6' },
      // RATIOs
      conversionRate: { name: getLabel('taux_conv', libelleTauxConversion(conversionConfig)), fill: '#f59e0b' },
      bounceRate: { name: getLabel('rebond', '% de Rebond'), fill: '#8b5cf6' },
      avgTimeOnSite: { name: getLabel('duree_moy', 'Durée moyenne'), fill: '#f97316' },
      actionsPerVisit: { name: getLabel('pages_visite', 'Pages/Session'), fill: '#14b8a6' },
      panierMoyen: { name: getLabel('panier_moyen', 'Panier moyen'), fill: '#ec4899' }
    };
    const config = configs[selectedBarMetric] || configs.conversions;
    return { dataKey: 'barMetricValue', ...config };
  }, [selectedBarMetric, conversionConfig, resultsMetrics.labels]);

  // Si en chargement ou pas de données, retourner null (l'écran de chargement est géré par GA4DashboardView)
  if (loading || !summary) {
    return null;
  }

  return (
    <>
      <div className="section-header">
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TrendingUp size={20} />
          Résultats
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Boutons de granularité */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {[
              { value: 'day', label: 'Jour' },
              { value: 'week', label: 'Semaine' },
              { value: 'month', label: 'Mois' },
              { value: 'quarter', label: 'Trimestre' },
              { value: 'year', label: 'Année' }
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => onGranularityChange?.(value)}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: granularity === value ? '#1a1a1a' : 'white',
                  color: granularity === value ? 'white' : '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                title={`Afficher par ${label.toLowerCase()}`}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Bouton settings métriques (inclut la config des conversions) */}
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Résultats Google Analytics"
            />
          ) : (
            <button
              onClick={() => setShowPerformanceChart(!showPerformanceChart)}
              className="section-toggle-btn"
              title={showPerformanceChart ? "Masquer la section" : "Afficher la section"}
            >
              {showPerformanceChart ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {showPerformanceChart && (
        <>
          {/* Message si pas de données */}
          {(!data || data.length === 0) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px',
              backgroundColor: '#f3f4f6',
              borderRadius: '8px',
              marginBottom: '12px',
              color: '#6b7280',
              fontSize: '14px',
              border: '1px solid #e5e7eb'
            }}>
              <AlertCircle size={16} style={{ marginRight: '8px', opacity: 0.6 }} />
              Aucune donnée disponible pour cette période
            </div>
          )}

          {/* Graphique */}
          <div className="full-width-chart" style={{
            width: '100%',
            minWidth: '300px',
            height: '400px',
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}>
            <ResponsiveContainer width="100%" height="100%" debounce={50}>
              <ComposedChart
                data={chartData}
                margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  type="category"
                  allowDuplicatedCategory={false}
                  tickFormatter={(value) => formatDateLabel(value, granularity)}
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  stroke={currentLineConfig.color}
                  tick={{ fontSize: 12 }}
                  label={{ value: currentLineConfig.label, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#8884d8"
                  tick={{ fontSize: 12 }}
                  label={{ value: barMetricConfig.name, angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value, name) => {
                    if (name === currentLineConfig.label) {
                      return [currentLineConfig.format(value) + currentLineConfig.unit, name];
                    }
                    /*
                      La série en barres n'avait aucun formatage propre : elle retombait sur
                      `formatNumber(value)`, c'est-à-dire un entier. Toute métrique de ratio
                      choisie pour l'histogramme s'affichait donc « 0 » dans l'infobulle — un
                      CPC de 0,0164 € ou un coût par interaction de 0,016 €. La carte, elle,
                      montrait 0,02 €, et les deux se contredisaient à l'écran.

                      Le formatage est repris de `lineMetricConfig`, la table qui porte déjà
                      l'unité et le nombre de décimales de chaque métrique. Une seconde table
                      propre aux barres aurait fini par diverger de la première.
                    */
                    const configBarre = lineMetricConfig[selectedBarMetric];
                    if (configBarre && name === barMetricConfig.name) {
                      return [configBarre.format(value) + configBarre.unit, name];
                    }
                    return [formatNumber(value), name];
                  }}
                />
                <Legend />

                {/* Repères des notes. Placés après la légende pour passer au-dessus des
                    séries, comme sur les dashboards de régies. */}
                {marqueursDeNotes({
                  notes,
                  donneesDuGraphique: chartData,
                  granularite: granularity,
                  onSurvol,
                  onSortie
                })}

                {/* Barres */}
                <Bar
                  yAxisId="right"
                  dataKey={barMetricConfig.dataKey}
                  fill={barMetricConfig.fill}
                  fillOpacity={0.8}
                  name={barMetricConfig.name}
                  radius={[4, 4, 0, 0]}
                />

                {/* Courbe */}
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="lineMetricValue"
                  stroke={currentLineConfig.color}
                  strokeWidth={3}
                  name={currentLineConfig.label}
                  dot={{ fill: currentLineConfig.color, strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7, fill: currentLineConfig.color }}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>

          {/* L'infobulle est en position fixe : hors du graphique, elle n'est pas rognée
              par ses marges. */}
          <InfobulleDeNotes survolee={survolee} position={position} />
          </div>

          {/* KPI Cards - Grille 4 colonnes comme Google Ads */}
          <div className="chart-insights" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '12px',
            marginTop: '16px'
          }}>
            {/* Afficher les KPI Cards dans l'ordre configuré */}
            {resultsMetrics.order.map(metricKey => {
              // Ne pas afficher si la métrique n'est pas active
              if (!isResultsMetricActive(metricKey)) return null;

              // Obtenir le label personnalisé ou par défaut
              const getLabel = (key, defaultLabel) => resultsMetrics.labels[key] || defaultLabel;

              // Rendu selon la métrique
              switch (metricKey) {
                case 'visites':
                  return (
                    <div
                      key={metricKey}
                      onClick={() => {
                        if (selectedBarMetric === 'visits') {
                          const oldLineMetric = selectedLineMetric;
                          setSelectedLineMetric('visits');
                          if (oldLineMetric) setSelectedBarMetric(oldLineMetric);
                          return;
                        }
                        const oldBarMetric = selectedBarMetric;
                        setSelectedBarMetric('visits');
                        if (oldBarMetric) setSelectedLineMetric(oldBarMetric);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        outline: selectedBarMetric === 'visits'
                          ? '3px solid #82ca9d'
                          : selectedLineMetric === 'visits'
                            ? '3px solid #82ca9d'
                            : 'none',
                        outlineOffset: '2px',
                        transition: 'outline 0.2s'
                      }}
                      title={selectedBarMetric === 'visits' ? 'Affiché en barres' : selectedLineMetric === 'visits' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                    >
                      <KPICard
                        title={getLabel('visites', 'Sessions')}
                        value={kpiData.totalVisits}
                        color="#82ca9d"
                        currentPeriodData={data?.map(d => d.visits) || []}
                        previousValue={prevSummary?.totalVisits}
                        comparisonLabel={comparisonLabel}
                      />
                    </div>
                  );

                case 'visiteurs':
                  return (
                    <div
                      key={metricKey}
                      onClick={() => {
                        if (selectedBarMetric === 'visitors') {
                          const oldLineMetric = selectedLineMetric;
                          setSelectedLineMetric('visitors');
                          if (oldLineMetric) setSelectedBarMetric(oldLineMetric);
                          return;
                        }
                        const oldBarMetric = selectedBarMetric;
                        setSelectedBarMetric('visitors');
                        if (oldBarMetric) setSelectedLineMetric(oldBarMetric);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        outline: selectedBarMetric === 'visitors'
                          ? '3px solid #1a1a1a'
                          : selectedLineMetric === 'visitors'
                            ? '3px solid #1a1a1a'
                            : 'none',
                        outlineOffset: '2px',
                        transition: 'outline 0.2s'
                      }}
                      title={selectedBarMetric === 'visitors' ? 'Affiché en barres' : selectedLineMetric === 'visitors' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                    >
                      <KPICard
                        title={getLabel('visiteurs', 'Utilisateurs')}
                        value={kpiData.totalUsers}
                        color="#1a1a1a"
                        currentPeriodData={data?.map(d => d.uniqueVisitors || d.users || d.activeUsers || 0) || []}
                        previousValue={prevSummary?.totalUsers || prevSummary?.activeUsers}
                        comparisonLabel={comparisonLabel}
                      />
                    </div>
                  );

                case 'taux_conv':
                  return (
                    <div
                      key={metricKey}
                      onClick={() => {
                        if (selectedBarMetric === 'conversionRate') {
                          const oldLineMetric = selectedLineMetric;
                          setSelectedLineMetric('conversionRate');
                          if (oldLineMetric) setSelectedBarMetric(oldLineMetric);
                          return;
                        }
                        const oldBarMetric = selectedBarMetric;
                        setSelectedBarMetric('conversionRate');
                        if (oldBarMetric) setSelectedLineMetric(oldBarMetric);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        outline: selectedBarMetric === 'conversionRate'
                          ? '3px solid #f59e0b'
                          : selectedLineMetric === 'conversionRate'
                            ? '3px solid #f59e0b'
                            : 'none',
                        outlineOffset: '2px',
                        transition: 'outline 0.2s'
                      }}
                      title={selectedBarMetric === 'conversionRate' ? 'Affiché en barres' : selectedLineMetric === 'conversionRate' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                    >
                      <KPICard
                        title={getLabel('taux_conv', libelleTauxConversion(conversionConfig))}
                        value={kpiData.conversionRate}
                        unit="%"
                        color="#f59e0b"
                        currentPeriodData={data?.map(d => d.visits > 0 ? (d.conversions || 0) / d.visits * 100 : 0) || []}
                        previousValue={prevSummary?.conversionRate}
                        formatter={(val) => formatNumber(val, 2)}
                        comparisonLabel={comparisonLabel}
                        className={getMetricObjectiveClassName('taux_conv', kpiData.conversionRate, 'ga4_results', accountId)}
                      />
                    </div>
                  );

                case 'conv':
                  return (
                    <div
                      key={metricKey}
                      onClick={() => {
                        if (selectedBarMetric === 'conversions') {
                          const oldLineMetric = selectedLineMetric;
                          setSelectedLineMetric('conversions');
                          if (oldLineMetric) setSelectedBarMetric(oldLineMetric);
                          return;
                        }
                        const oldBarMetric = selectedBarMetric;
                        setSelectedBarMetric('conversions');
                        if (oldBarMetric) setSelectedLineMetric(oldBarMetric);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        outline: selectedBarMetric === 'conversions'
                          ? '3px solid #8884d8'
                          : selectedLineMetric === 'conversions'
                            ? '3px solid #8884d8'
                            : 'none',
                        outlineOffset: '2px',
                        transition: 'outline 0.2s'
                      }}
                      title={selectedBarMetric === 'conversions' ? 'Affiché en barres' : selectedLineMetric === 'conversions' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                    >
                      <KPICard
                        title={getLabel('conv', libelleConversions(conversionConfig))}
                        value={kpiData.totalConversions}
                        color="#8884d8"
                        currentPeriodData={data?.map(d => d.conversions || 0) || []}
                        previousValue={prevSummary?.conversions}
                        comparisonLabel={comparisonLabel}
                      />
                    </div>
                  );

                case 'pages_vues':
                  return (
                    <div
                      key={metricKey}
                      onClick={() => {
                        if (selectedBarMetric === 'pageviews') {
                          const oldLineMetric = selectedLineMetric;
                          setSelectedLineMetric('pageviews');
                          if (oldLineMetric) setSelectedBarMetric(oldLineMetric);
                          return;
                        }
                        const oldBarMetric = selectedBarMetric;
                        setSelectedBarMetric('pageviews');
                        if (oldBarMetric) setSelectedLineMetric(oldBarMetric);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        outline: selectedBarMetric === 'pageviews'
                          ? '3px solid #10b981'
                          : selectedLineMetric === 'pageviews'
                            ? '3px solid #10b981'
                            : 'none',
                        outlineOffset: '2px',
                        transition: 'outline 0.2s'
                      }}
                      title={selectedBarMetric === 'pageviews' ? 'Affiché en barres' : selectedLineMetric === 'pageviews' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                    >
                      <KPICard
                        title={getLabel('pages_vues', 'Pages vues')}
                        value={kpiData.totalPageviews}
                        color="#10b981"
                        currentPeriodData={data?.map(d => d.pageviews) || []}
                        previousValue={prevSummary?.totalPageviews}
                        comparisonLabel={comparisonLabel}
                      />
                    </div>
                  );

                case 'rebond':
                  return (
                    <div
                      key={metricKey}
                      onClick={() => {
                        if (selectedBarMetric === 'bounceRate') {
                          const oldLineMetric = selectedLineMetric;
                          setSelectedLineMetric('bounceRate');
                          if (oldLineMetric) setSelectedBarMetric(oldLineMetric);
                          return;
                        }
                        const oldBarMetric = selectedBarMetric;
                        setSelectedBarMetric('bounceRate');
                        if (oldBarMetric) setSelectedLineMetric(oldBarMetric);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        outline: selectedBarMetric === 'bounceRate'
                          ? '3px solid #8b5cf6'
                          : selectedLineMetric === 'bounceRate'
                            ? '3px solid #8b5cf6'
                            : 'none',
                        outlineOffset: '2px',
                        transition: 'outline 0.2s'
                      }}
                      title={selectedBarMetric === 'bounceRate' ? 'Affiché en barres' : selectedLineMetric === 'bounceRate' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                    >
                      <KPICard
                        title={getLabel('rebond', '% de Rebond')}
                        value={kpiData.avgBounceRate}
                        unit="%"
                        color="#8b5cf6"
                        currentPeriodData={data?.map(d => d.bounceRate || 0) || []}
                        previousValue={prevSummary?.bounceRate}
                        formatter={(val) => formatNumber(val, 1)}
                        invertVariation={true}
                        comparisonLabel={comparisonLabel}
                        className={getMetricObjectiveClassName('rebond', kpiData.avgBounceRate, 'ga4_results', accountId)}
                      />
                    </div>
                  );

                case 'duree_moy':
                  return (
                    <div
                      key={metricKey}
                      onClick={() => {
                        if (selectedBarMetric === 'avgTimeOnSite') {
                          const oldLineMetric = selectedLineMetric;
                          setSelectedLineMetric('avgTimeOnSite');
                          if (oldLineMetric) setSelectedBarMetric(oldLineMetric);
                          return;
                        }
                        const oldBarMetric = selectedBarMetric;
                        setSelectedBarMetric('avgTimeOnSite');
                        if (oldBarMetric) setSelectedLineMetric(oldBarMetric);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        outline: selectedBarMetric === 'avgTimeOnSite'
                          ? '3px solid #f97316'
                          : selectedLineMetric === 'avgTimeOnSite'
                            ? '3px solid #f97316'
                            : 'none',
                        outlineOffset: '2px',
                        transition: 'outline 0.2s'
                      }}
                      title={selectedBarMetric === 'avgTimeOnSite' ? 'Affiché en barres' : selectedLineMetric === 'avgTimeOnSite' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                    >
                      <KPICard
                        title={getLabel('duree_moy', 'Durée moyenne')}
                        value={kpiData.avgTimeOnSite}
                        color="#f97316"
                        currentPeriodData={data?.map(d => d.avgTimeOnSite || 0) || []}
                        previousValue={prevSummary?.avgTimeOnSite}
                        formatter={(val) => formatTime(val)}
                        comparisonLabel={comparisonLabel}
                        className={getMetricObjectiveClassName('duree_moy', kpiData.avgTimeOnSite, 'ga4_results', accountId)}
                      />
                    </div>
                  );

                case 'panier_moyen':
                  const avgOrderValue = kpiData.totalTransactions > 0
                    ? kpiData.totalRevenue / kpiData.totalTransactions
                    : 0;
                  const prevAvgOrderValue = prevSummary && prevSummary.transactions > 0
                    ? (prevSummary.totalRevenue || 0) / prevSummary.transactions
                    : null;
                  return (
                    <div
                      key={metricKey}
                      onClick={() => {
                        if (selectedBarMetric === 'panierMoyen') {
                          const oldLineMetric = selectedLineMetric;
                          setSelectedLineMetric('panierMoyen');
                          if (oldLineMetric) setSelectedBarMetric(oldLineMetric);
                          return;
                        }
                        const oldBarMetric = selectedBarMetric;
                        setSelectedBarMetric('panierMoyen');
                        if (oldBarMetric) setSelectedLineMetric(oldBarMetric);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        outline: selectedBarMetric === 'panierMoyen'
                          ? '3px solid #a855f7'
                          : selectedLineMetric === 'panierMoyen'
                            ? '3px solid #a855f7'
                            : 'none',
                        outlineOffset: '2px',
                        transition: 'outline 0.2s'
                      }}
                      title={selectedBarMetric === 'panierMoyen' ? 'Affiché en barres' : selectedLineMetric === 'panierMoyen' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                    >
                      <KPICard
                        title={getLabel('panier_moyen', 'Panier moyen')}
                        value={avgOrderValue}
                        unit="€"
                        color="#a855f7"
                        currentPeriodData={data?.map(d => d.conversions > 0 ? (d.conversionsValue || 0) / d.conversions : 0) || []}
                        previousValue={prevAvgOrderValue}
                        formatter={(val) => formatNumber(val, 2)}
                        comparisonLabel={comparisonLabel}
                        className={getMetricObjectiveClassName('panier_moyen', avgOrderValue, 'ga4_results', accountId)}
                      />
                    </div>
                  );

                case 'pages_visite':
                  return (
                    <div
                      key={metricKey}
                      onClick={() => {
                        if (selectedBarMetric === 'actionsPerVisit') {
                          const oldLineMetric = selectedLineMetric;
                          setSelectedLineMetric('actionsPerVisit');
                          if (oldLineMetric) setSelectedBarMetric(oldLineMetric);
                          return;
                        }
                        const oldBarMetric = selectedBarMetric;
                        setSelectedBarMetric('actionsPerVisit');
                        if (oldBarMetric) setSelectedLineMetric(oldBarMetric);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        outline: selectedBarMetric === 'actionsPerVisit'
                          ? '3px solid #ec4899'
                          : selectedLineMetric === 'actionsPerVisit'
                            ? '3px solid #ec4899'
                            : 'none',
                        outlineOffset: '2px',
                        transition: 'outline 0.2s'
                      }}
                      title={selectedBarMetric === 'actionsPerVisit' ? 'Affiché en barres' : selectedLineMetric === 'actionsPerVisit' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                    >
                      <KPICard
                        title={getLabel('pages_visite', 'Pages/Session')}
                        value={kpiData.avgActionsPerVisit}
                        color="#ec4899"
                        currentPeriodData={data?.map(d => d.actionsPerVisit || 0) || []}
                        previousValue={prevSummary?.actionsPerVisit}
                        formatter={(val) => formatNumber(val, 1)}
                        comparisonLabel={comparisonLabel}
                        className={getMetricObjectiveClassName('pages_visite', kpiData.avgActionsPerVisit, 'ga4_results', accountId)}
                      />
                    </div>
                  );

                case 'transactions':
                  return (
                    <div
                      key={metricKey}
                      onClick={() => {
                        if (selectedBarMetric === 'transactions') {
                          const oldLineMetric = selectedLineMetric;
                          setSelectedLineMetric('transactions');
                          if (oldLineMetric) setSelectedBarMetric(oldLineMetric);
                          return;
                        }
                        const oldBarMetric = selectedBarMetric;
                        setSelectedBarMetric('transactions');
                        if (oldBarMetric) setSelectedLineMetric(oldBarMetric);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        outline: selectedBarMetric === 'transactions'
                          ? '3px solid #10b981'
                          : selectedLineMetric === 'transactions'
                            ? '3px solid #10b981'
                            : 'none',
                        outlineOffset: '2px',
                        transition: 'outline 0.2s'
                      }}
                      title={selectedBarMetric === 'transactions' ? 'Affiché en barres' : selectedLineMetric === 'transactions' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                    >
                      <KPICard
                        title={getLabel('transactions', 'Transactions')}
                        value={kpiData.totalTransactions}
                        color="#10b981"
                        currentPeriodData={data?.map(d => d.transactions || 0) || []}
                        previousValue={prevSummary?.transactions}
                        comparisonLabel={comparisonLabel}
                        className={getMetricObjectiveClassName('transactions', kpiData.totalTransactions, 'ga4_results', accountId)}
                      />
                    </div>
                  );

                case 'chiffre_affaires':
                  return (
                    <div
                      key={metricKey}
                      onClick={() => {
                        if (selectedBarMetric === 'totalRevenue') {
                          const oldLineMetric = selectedLineMetric;
                          setSelectedLineMetric('totalRevenue');
                          if (oldLineMetric) setSelectedBarMetric(oldLineMetric);
                          return;
                        }
                        const oldBarMetric = selectedBarMetric;
                        setSelectedBarMetric('totalRevenue');
                        if (oldBarMetric) setSelectedLineMetric(oldBarMetric);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        outline: selectedBarMetric === 'totalRevenue'
                          ? '3px solid #f59e0b'
                          : selectedLineMetric === 'totalRevenue'
                            ? '3px solid #f59e0b'
                            : 'none',
                        outlineOffset: '2px',
                        transition: 'outline 0.2s'
                      }}
                      title={selectedBarMetric === 'totalRevenue' ? 'Affiché en barres' : selectedLineMetric === 'totalRevenue' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                    >
                      <KPICard
                        title={getLabel('chiffre_affaires', 'Chiffre d\'affaires')}
                        value={kpiData.totalRevenue}
                        unit="€"
                        color="#f59e0b"
                        currentPeriodData={data?.map(d => d.totalRevenue || 0) || []}
                        previousValue={prevSummary?.totalRevenue}
                        formatter={(val) => formatNumber(val, 2)}
                        comparisonLabel={comparisonLabel}
                        className={getMetricObjectiveClassName('chiffre_affaires', kpiData.totalRevenue, 'ga4_results', accountId)}
                      />
                    </div>
                  );

                case 'taux_transaction':
                  return (
                    <div
                      key={metricKey}
                      onClick={() => {
                        if (selectedBarMetric === 'transactionRate') {
                          const oldLineMetric = selectedLineMetric;
                          setSelectedLineMetric('transactionRate');
                          if (oldLineMetric) setSelectedBarMetric(oldLineMetric);
                          return;
                        }
                        const oldBarMetric = selectedBarMetric;
                        setSelectedBarMetric('transactionRate');
                        if (oldBarMetric) setSelectedLineMetric(oldBarMetric);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        outline: selectedBarMetric === 'transactionRate'
                          ? '3px solid #14b8a6'
                          : selectedLineMetric === 'transactionRate'
                            ? '3px solid #14b8a6'
                            : 'none',
                        outlineOffset: '2px',
                        transition: 'outline 0.2s'
                      }}
                      title={selectedBarMetric === 'transactionRate' ? 'Affiché en barres' : selectedLineMetric === 'transactionRate' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                    >
                      <KPICard
                        title={getLabel('taux_transaction', '% Conversion (transactions)')}
                        value={kpiData.transactionRate}
                        unit="%"
                        color="#14b8a6"
                        currentPeriodData={data?.map(d => d.visits > 0 ? (d.transactions || 0) / d.visits * 100 : 0) || []}
                        previousValue={prevSummary?.transactionRate}
                        formatter={(val) => formatNumber(val, 2)}
                        comparisonLabel={comparisonLabel}
                        className={getMetricObjectiveClassName('taux_transaction', kpiData.transactionRate, 'ga4_results', accountId)}
                      />
                    </div>
                  );

                default:
                  return null;
              }
            })}
          </div>
        </>
      )}

      {/* Modal de configuration des métriques (inclut la config des conversions) */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="ga4_results"
        componentTitle="Résultats Google Analytics"
        accountId={accountId}
        token={token}
        propertyId={propertyId}
        currentAdvancedConfig={conversionConfig}
        onAdvancedConfigSave={onConversionConfigSave}
      />
    </>
  );
};

export default GA4PerformanceChart;
